import { Check, ChevronDown, Cloud, Cpu, Lock } from "lucide-react";
import * as React from "react";

import { llmApi, type ProviderInfo, type ProvidersResponse } from "@/api/llm";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chat";

/**
 * LLM Selector — dropdown chọn provider + model cho mỗi conversation.
 *
 * Hiển thị Local providers trước (free) rồi cloud. Disabled providers vẫn
 * show với reason để user biết cần cấu hình API key.
 */
export function LLMSelector(): JSX.Element {
  const [data, setData] = React.useState<ProvidersResponse | null>(null);
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const selectedProvider = useChatStore((s) => s.selectedProvider);
  const selectedModel = useChatStore((s) => s.selectedModel);
  const setLLM = useChatStore((s) => s.setLLM);

  // Load providers on mount
  React.useEffect(() => {
    void (async () => {
      try {
        const res = await llmApi.listProviders();
        setData(res);
        // Init default nếu chưa có
        if (!selectedProvider && !selectedModel) {
          setLLM(res.default_provider, res.default_model);
        }
      } catch {
        // Silently fail — Composer sẽ disable LLM nếu fail
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!data) {
    return (
      <span className="text-[10px] text-muted-foreground">Đang tải mô hình...</span>
    );
  }

  const currentProvider = data.providers.find((p) => p.id === selectedProvider);
  const currentModel = currentProvider?.models.find((m) => m.id === selectedModel);

  const label = currentProvider
    ? `${providerIcon(currentProvider)} ${currentModel?.label ?? selectedModel}`
    : "Chọn mô hình";

  return (
    <div className="relative inline-block" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs gap-1.5 px-2 font-normal"
        onClick={() => setOpen((v) => !v)}
        aria-label="Chọn mô hình AI"
      >
        <span className="truncate max-w-[200px]">{label}</span>
        <ChevronDown className="size-3" />
      </Button>

      {open ? (
        <div className="absolute bottom-full mb-1 left-0 z-50 w-[340px] max-h-[400px] overflow-y-auto rounded-md border bg-popover shadow-md animate-fade-in">
          {data.providers.map((p) => (
            <div key={p.id} className="border-b last:border-b-0">
              <div className="px-3 py-1.5 bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                {p.type === "local" ? (
                  <Cpu className="size-3" />
                ) : (
                  <Cloud className="size-3" />
                )}
                {p.label}
                {!p.available ? (
                  <span title={p.available_reason ?? ""}>
                    <Lock className="size-3 ml-auto text-muted-foreground" />
                  </span>
                ) : null}
              </div>
              {!p.available && p.available_reason ? (
                <div className="px-3 py-1 text-[10px] text-muted-foreground italic">
                  {p.available_reason}
                </div>
              ) : null}
              {p.models.map((m) => {
                const isSelected =
                  selectedProvider === p.id && selectedModel === m.id;
                return (
                  <button
                    type="button"
                    key={`${p.id}:${m.id}`}
                    disabled={!p.available}
                    onClick={() => {
                      setLLM(p.id, m.id);
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm transition-colors flex items-start gap-2",
                      "hover:bg-accent hover:text-accent-foreground",
                      "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
                      isSelected && "bg-accent",
                    )}
                  >
                    <span
                      className={cn(
                        "size-3.5 mt-0.5 flex-shrink-0",
                        isSelected ? "text-primary" : "text-transparent",
                      )}
                    >
                      <Check className="size-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-1.5">
                        {m.label}
                        {m.context_window ? (
                          <span className="text-[10px] text-muted-foreground font-normal">
                            ({(m.context_window / 1000).toFixed(0)}K)
                          </span>
                        ) : null}
                      </div>
                      {m.description ? (
                        <div className="text-[10px] text-muted-foreground leading-snug">
                          {m.description}
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function providerIcon(p: ProviderInfo): string {
  if (p.type === "local") return "🏠";
  if (p.id === "anthropic") return "🟠";
  if (p.id === "openai") return "🟢";
  if (p.id === "google") return "🔵";
  if (p.id === "deepseek") return "🐳";
  return "☁️";
}
